import React, { useEffect, useMemo, useRef, useState } from "react";
import { httpGet } from "../../components/helpers/httpMethods";
import "./conLanding.css";

// Change of Name - landing and newspaper comparison.
// Uses the same list as before (con/vendors). "Apply" opens the existing application form until the new one replaces it.
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const naira = (v) => `₦${Math.round(Number(v) || 0).toLocaleString("en-NG")}`;
const titleCase = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : String(s || "").trim());
const turnDays = (t) => { const n = parseInt(String(t || ""), 10); return Number.isFinite(n) && n > 0 ? n : 3; };
const days = (d) => { try { const a = Array.isArray(d) ? d : JSON.parse(d || "[]"); return Array.isArray(a) ? a.map((x) => String(x).trim()) : []; } catch (e) { return []; } };
const earliest = (o) => {
  const on = days(o.publishDays).map((d) => DAYS.findIndex((x) => x.toLowerCase() === d.toLowerCase())).filter((i) => i >= 0);
  const d = new Date();
  d.setDate(d.getDate() + turnDays(o.turnAroundTime));
  for (let i = 0; i < 7 && on.length && !on.includes(d.getDay()); i += 1) d.setDate(d.getDate() + 1);
  return d;
};
const shortDate = (d) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Africa/Lagos" });


// The four kinds of notice (same categories as the application form)
const CATS = [
  { key: "Change of Name", icon: "✍️", title: "Change of Name", what: "You’re taking a new name: after marriage, for your faith, or by personal choice.",
    need: "A sworn affidavit. After marriage, also your marriage certificate.",
    sample: "I, formerly known and addressed as Miss Adaeze Okafor, now wish to be known and addressed as Mrs Adaeze Bello." },
  { key: "Change of Name/Age Declaration", icon: "📅", title: "Change of Name / Age Declaration", what: "Change your name and declare your correct date of birth in one notice.",
    need: "A sworn affidavit covering your name and date of birth.",
    sample: "I, formerly known as Adaeze Okafor, now wish to be known as Adaeze Bello, and my correct date of birth is 12th March 1990." },
  { key: "Confirmation of Name", icon: "✅", title: "Confirmation of Name", what: "Your name appears differently on your documents and you want to confirm both refer to you.",
    need: "A sworn affidavit confirming both versions of your name.",
    sample: "I, Adaeze Ngozi Okafor, also known as Ada Okafor, wish to confirm that both names refer to one and the same person." },
  { key: "Rearrangement of Name", icon: "🔁", title: "Rearrangement of Name", what: "You’re keeping the same names but changing their order.",
    need: "A sworn affidavit showing the old and new order.",
    sample: "I, formerly known and addressed as Okafor Adaeze Ngozi, now wish to be known and addressed as Adaeze Ngozi Okafor." },
];

const FAQ = [
  ["What do I need before I apply?", "A sworn affidavit of change of name from a court or commissioner for oaths, and a valid ID. You’ll upload a clear photo or PDF of each. For a change after marriage, a marriage certificate helps; after divorce, the decree."],
  ["Which newspaper should I choose?", "Any national newspaper on this page is widely recognised. If a bank, school or government office asked for a specific paper, choose that one. Otherwise pick by price and how soon you need it."],
  ["How do I prove it was published?", "When the paper runs your notice, we attach the printed page to your application. You can download it, with your receipt, from My applications."],
  ["What if I make a mistake in my details?", "You see your notice exactly as it will print before you pay, so you can check every spelling. If something is still wrong after paying, contact us before the publication date."],
];

export default function ConLanding({ history }) {
  const [offers, setOffers] = useState(null);
  const [sort, setSort] = useState("price");
  const [open, setOpen] = useState(0);
  const [cat, setCat] = useState(() => { try { return localStorage.getItem("tns-con-category") || ""; } catch (e) { return ""; } });
  const listRef = useRef(null);
  useEffect(() => {
    httpGet("con/vendors").then((r) => setOffers((r && r.data && r.data.conSubscriptions) || []));
    window.scrollTo(0, 0);
  }, []);
  const list = useMemo(() => {
    const l = (offers || []).filter((o) => o.publication).map((o) => ({ ...o, total: Number(o.price) + Number(o.serviceCharge || 0), date: earliest(o) }));
    return l.sort((a, b) => (sort === "fast" ? a.date - b.date || a.total - b.total : a.total - b.total));
  }, [offers, sort]);
  const from = list.length ? Math.min(...list.map((o) => o.total)) : 0;
  const goList = () => listRef.current && listRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  const apply = (o) => history.push(`/changeofname/apply/${o.publicationId}`);
  const choose = (k) => { setCat(k); try { localStorage.setItem("tns-con-category", k); } catch (e) { /* ignore */ } setTimeout(goList, 50); };
  const chosen = CATS.find((c) => c.key === cat);

  return (
    <div className="cn-page">
      <section className="cn-hero">
        <div className="cn-wrap cn-hero-in">
          <span className="cn-kicker">Change of Name</span>
          <h1>Publish your change of name in a national newspaper</h1>
          <p>The published notice is what banks, passport offices, NIMC and employers ask for. Apply online in about 10 minutes, pay once, and download the printed page as proof.</p>
          <div className="cn-hero-actions">
            <button type="button" className="cn-btn" onClick={goList}>Choose a newspaper{from ? ` · from ${naira(from)}` : ""}</button>
            <button type="button" className="cn-btn2" onClick={() => history.push("/account/applications")}>Track my applications</button>
          </div>
        </div>
      </section>

      <div className="cn-wrap">
        <section className="cn-steps" aria-label="How it works">
          {[["1", "Fill in your details", "Old name, new name and reason. See your notice exactly as it will print."],
            ["2", "Upload your documents", "A clear photo or PDF of your sworn affidavit and a valid ID."],
            ["3", "Pay and track", "The newspaper publishes it and you download the printed page as proof."]].map(([n, t, d]) => (
            <div className="cn-card cn-step" key={n}><span className="cn-num">{n}</span><strong>{t}</strong><p>{d}</p></div>
          ))}
        </section>

        <section className="cn-facts">
          <div className="cn-fact"><span aria-hidden="true">📄</span><span><strong>You’ll need</strong><small>A sworn affidavit and a valid ID (photo or PDF).</small></span></div>
          <div className="cn-fact"><span aria-hidden="true">⏱</span><span><strong>How long</strong><small>Most papers publish within 3 to 7 days of payment.</small></span></div>
          <div className="cn-fact"><span aria-hidden="true">✅</span><span><strong>Proof you can use</strong><small>Download the printed page and your receipt.</small></span></div>
        </section>

        <section className="cn-cats" aria-label="What kind of notice">
          <div className="cn-cats-head"><h2>What kind of notice do you need?</h2><p>Choose yours to see what it means and what you’ll need. Every notice ends with “All former documents remain valid. General public please take note.”</p></div>
          <div className="cn-cat-grid">
            {CATS.map((c) => (
              <button type="button" key={c.key} className={`cn-card cn-cat${cat === c.key ? " on" : ""}`} onClick={() => choose(c.key)} aria-pressed={cat === c.key}>
                <span className="cn-cat-top"><span className="cn-cat-icon" aria-hidden="true">{c.icon}</span><strong>{c.title}</strong></span>
                <span className="cn-cat-what">{c.what}</span>
                <span className="cn-cat-need"><b>You’ll need:</b> {c.need}</span>
                <span className="cn-cat-sample"><span>Example notice</span>{c.sample}</span>
                <span className="cn-cat-go">{cat === c.key ? "Selected ✓ – now choose a newspaper" : "Choose this ›"}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="cn-list" ref={listRef} aria-label="Choose a newspaper">
          <div className="cn-list-head">
            <div><h2>Choose a newspaper</h2><p>The price is everything you pay. The date is the earliest your notice can appear if you apply today.</p>{chosen ? <p className="cn-chosen">Your notice: <b>{chosen.title}</b> <button type="button" onClick={() => document.querySelector(".cn-cats").scrollIntoView({ behavior: "smooth" })}>Change</button></p> : null}</div>
            <div className="cn-tabs" role="tablist" aria-label="Sort">
              <button type="button" role="tab" aria-selected={sort === "price"} className={sort === "price" ? "on" : ""} onClick={() => setSort("price")}>Lowest price</button>
              <button type="button" role="tab" aria-selected={sort === "fast"} className={sort === "fast" ? "on" : ""} onClick={() => setSort("fast")}>Soonest</button>
            </div>
          </div>
          {offers === null ? <p className="cn-muted">Loading newspapers…</p> : !list.length ? <p className="cn-muted">No newspapers are taking Change of Name notices right now. Please check back soon.</p> : (
            <div className="cn-rows">
              {list.map((o) => {
                const on = days(o.publishDays).map((d) => d.slice(0, 3).toLowerCase());
                return (
                  <div className="cn-card cn-row" key={o.id}>
                    <span className="cn-logo">{o.publication.logo || o.publication.image ? <img src={o.publication.logo || o.publication.image} alt="" loading="lazy" /> : null}</span>
                    <span className="cn-row-main">
                      <strong>{titleCase(o.publication.name)}</strong>
                      <span className="cn-days" aria-label={`Publishes on ${days(o.publishDays).join(", ")}`}>
                        {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => <span key={d} className={on.includes(d) ? "on" : ""}>{d[0].toUpperCase() + d.slice(1)}</span>)}
                      </span>
                      <small>Publishes in about {turnDays(o.turnAroundTime)} days</small>
                    </span>
                    <span className="cn-row-date"><small>Earliest</small><strong>{shortDate(o.date)}</strong></span>
                    <span className="cn-row-price"><strong>{naira(o.total)}</strong></span>
                    <button type="button" className="cn-btn" onClick={() => apply(o)}>Apply</button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="cn-faq" aria-label="Questions">
          <h2>Questions</h2>
          {FAQ.map(([q, a], i) => (
            <div className={`cn-card cn-q${open === i ? " open" : ""}`} key={q}>
              <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? -1 : i)}><span>{q}</span><span aria-hidden="true">{open === i ? "−" : "+"}</span></button>
              {open === i ? <p>{a}</p> : null}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
